<template>
  <section
    class="SeasonsPage"
    data-theme="light"
    :aria-busy="isFetching"
  >
    <Transition name="fade">
      <div v-if="homeSeasons.length > 0">
        <div
          v-if="isCommissioner"
          class="SeasonsPage__buttonContainer"
        >
          <FormButton
            iconName="calendar"
            @click="openModal({ name: RouteName.seasonNew })"
          >
            Start a new season
          </FormButton>
        </div>
        <SeasonListView
          :seasons="homeSeasons"
        />
      </div>
      <ErrorEmptyState
        v-else-if="hasError"
        @retry="fetchSeasons()"
      />
      <EmptyState
        v-else-if="!isFetching"
        iconName="calendar"
      >
        <h2>Seasons are a series of regular runs</h2>
        <p>
          <template v-if="isCommissioner">
            Schedule multiple runs with the same time and location. Manage a roster of regulars while the rest of your crew joins a waitlist.
          </template>
          <template v-else>
            Ask your commissioner to create a season if you play every week
          </template>
        </p>
        <FormButton
          v-if="isCommissioner"
          @click="openModal({ name: RouteName.seasonNew })"
        >
          Start a season
        </FormButton>
      </EmptyState>
    </Transition>
  </section>
</template>

<script lang="ts" setup>
import EmptyState from "@/components/EmptyState.vue";
import ErrorEmptyState from "@/components/ErrorEmptyState.vue";
import FormButton from "@/components/FormButton.vue";
import useVisibilityChange from "@/composables/useVisibilityChange";
import { RouteName } from "@/lib/router/routes";
import useAuthStore from "@/stores/useAuthStore";
import useModalStore from "@/stores/useModalStore";
import useSeasonStore from "@/stores/useSeasonStore";
import SeasonListView from "@/views/SeasonListView.vue";
import { useSeoMeta } from "@unhead/vue";
import { storeToRefs } from "pinia";
import { onBeforeMount, ref } from "vue";

useSeoMeta({
  title: "Seasons",
});

const {
  openModal,
} = useModalStore();

const {
  isCommissioner,
} = storeToRefs(useAuthStore());

const {
  homeSeasons,
} = storeToRefs(useSeasonStore());

const {
  fetchHomeSeasons,
} = useSeasonStore();

const isFetching = ref(false);
const hasError = ref(false);

useVisibilityChange(handleVisibilityChange);

onBeforeMount(async () => {
  await fetchSeasons();
});

async function fetchSeasons () {
  if (isFetching.value) return;

  isFetching.value = true;
  hasError.value = false;

  const [error] = await fetchHomeSeasons();
  if (error) hasError.value = true;

  isFetching.value = false;
}

function handleVisibilityChange (isAppVisible: boolean) {
  if (isAppVisible) fetchSeasons();
}
</script>

<style lang="scss" scoped>
.SeasonsPage {
  display: grid;
  align-items: start;
  height: 100%;

  background-color: var(--themeColorSurface);
}

.SeasonsPage__buttonContainer {
  display: grid;
  padding: 16px;
  border-bottom: solid 1px var(--themeColorNeutral100);
}
</style>
